import http from "@services/httpService"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import {
  CustomerForMergeModel,
  CustomerModel,
  CustomerUpdateModel,
} from "@models/customer/customerModel"
import { CustomerPotentialModel } from "@models/customer/customerPotentialModel"

const getCustomerAuditFieldLabels = (): Record<string, string> => ({
  FullName:              L("CUSTOMER_DETAIL_FULL_NAME"),
  IdentifierCardNumber:  L("CUSTOMER_DETAIL_INDENTIFIER_CARD_NUMBER"),
  IdIssuedDate:          L("CUSTOMER_DETAIL_ID_ISSUED_DATE"),
  IdIssuedPlace:         L("CUSTOMER_DETAIL_ID_ISSUED_PLACE"),
  NationalityId:         L("CUSTOMER_DETAIL_NATIONALITY"),
  Position:              L("CUSTOMER_DETAIL_POSITION"),
  CustomerType:          L("CUSTOMER_EXISTING_CUSTOMER_TYPE"),
  Gender:                L("CUSTOMER_DETAIL_GENDER"),
  BirthDate:             L("CUSTOMER_POTENTIAL_BIRTH_DATE"),
  ConsultantId:          L("CUSTOMER_POTENTIAL_AGENT"),
  AgencyMasterId:        L("CONTRACT_BOOKING_DETAIL_AGENCY_MASTER"),
  Tags:                  L("CUSTOMER_POTENTIAL_TAGS"),
  BankAddress:           L("CUSTOMER_DETAIL_ADDRESS"),
  BankBranch:            L("CUSTOMER_DETAIL_BANK_BRANCH"),
  BankSwiftCode:         L("CUSTOMER_DETAIL_COMMONLY_USED_BANK"),
  BankName:              L("CUSTOMER_DETAIL_COMMONLY_USED_BANK"),
  BankNumber:            L("BOOKING_DETAIL_BANK_NUMBER"),
  Beneficiary:           L("CUSTOMER_DETAIL_BENEFICIARY"),
  NecessaryLevel:        L("CUSTOMER_POTENTIAL_NEED_LEVEL"),
})

class CustomerService {
  public async create(body: any) {
    const newBody = CustomerUpdateModel.assign(body)

    const res = await http.post(
      "api/services/app/ExistingCustomer/Create",
      newBody
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async update(body: any) {
    const newBody = CustomerUpdateModel.assign(body)

    const res = await http.put(
      "api/services/app/ExistingCustomer/Update",
      newBody
    )
    return res.data.result
  }

  /**
   * Feature-0381 — cập nhật KHHH có hiệu lực NGAY, không sinh phiếu duyệt.
   * Body giống `update`. `result.requestId` là vết kiểm toán đã Approved — không rẽ nhánh gửi duyệt.
   */
  public async updateDirect(body: any) {
    const newBody = CustomerUpdateModel.assign(body)
    const res = await http.put(
      "api/services/app/ExistingCustomer/UpdateDirect",
      newBody
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async activateOrDeactivate(id: number, isActive) {
    const res = await http.post(
      "api/services/app/ExistingCustomer/Active",
      { id },
      { params: { isActive } }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }

  public async bulkActive(id: number[]) {
    const res = await http.post(
      "api/services/app/ExistingCustomer/BulkActive",
      id
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("UPDATE_SUCCESSFULLY")
    )
    return res.data
  }
  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/ExistingCustomer/GetAll", {
      params,
    })
    const { result } = res.data

    result.items = CustomerModel.assigns(result.items)
    return result
  }

  public async getECI(params: any): Promise<any> {
    const res = await http.get("api/services/app/ExistingCustomer/GetECI", {
      params,
    })

    const { result } = res.data

    return result
  }

  public async get(id: number, isViewFull: boolean = false): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/ExistingCustomer/GetById", {
      params: { id, isViewFull },
    })
    const { result } = res.data
    return CustomerModel.assign(result)
  }

  public async getForMerge(id: number): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/ExistingCustomer/GetById", {
      params: { id },
    })
    const { result } = res.data
    return CustomerForMergeModel.assign(result)
  }
  public async mergeCustomer(body: any) {
    const res = await http.post(
      "api/services/app/ExistingCustomer/MergeCustomer",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async assignUserInGroup(body: any) {
    const res = await http.post(
      "api/services/app/ExistingCustomer/AssignUserInGroup",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("ASSIGN_SUCCESSFULLY")
    )
    return res.data.result
  }

  public async removeUserInGroup(params: {
    groupId: number
    customerIds: number[]
  }) {
    const res = await http.delete(
      "api/services/app/ExistingCustomer/RemoveUserInGroup",
      { params }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("REMOVE_SUCCESSFULLY")
    )
    return res.data.result
  }

  // contract

  public async getECIByIdentifier(params: {
    identifierCardNumber: string
    companyId: number
  }): Promise<any> {
    const res = await http.get(
      "api/services/app/ExistingCustomer/GetECIByIdentifier",
      { params }
    )
    const { result } = res.data
    return result
  }

  public async checkExist(params): Promise<any> {
    const res = await http.post(
      "api/services/app/ExistingCustomer/CheckExist",
      {},
      { params }
    )

    const { result } = res.data
    return result
  }

  public async checkMultiExist(body): Promise<any> {
    const res = await http.post(
      "api/services/app/ExistingCustomer/CheckMultiExist",
      body
    )

    const { result } = res.data
    return CustomerPotentialModel.assigns(result)
  }

  public async getAutoComplete(params): Promise<any> {
    const res = await http.post(
      "api/services/app/ExistingCustomer/AutoComplete",
      {},
      { params }
    )

    const { result } = res.data
    return CustomerPotentialModel.assigns(result)
  }

  // import
  public async importMigration(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)

    const result = await http.post(
      "api/Imports/Migration/ImportCustomer",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )

    return result.data.result
  }

  public async getAuditLog(id: number) {
    const res = await http.get(
      "api/services/app/ExistingCustomer/GetAuditLogs",
      { params: { id } }
    )
    const labels = getCustomerAuditFieldLabels()
    return (res.data.result || []).map((row: any) => {
      ;(row.items || []).forEach((item: any) => {
        item.propertyName = labels[item.propertyName] ?? item.propertyName
      })
      return row
    })
  }

  //activity
  public async getAllActivity(params: any) {
    const res = await http.get("/api/services/app/ExistingActivity/GetAll", {
      params,
    })
    const { result } = res.data
    return result
  }

  //get activity by user
  public async getActivityById(id: number) {
    const res = await http.get("/api/services/app/ExistingActivity/get", {
      params: { id },
    })
    const { result } = res.data
    return result
  }

  /*
  {
    "id": 0, // nếu không có thì là tạo mới, nếu có thì là cập nhật
    "title": "Gọi điện tư vấn",
    "description": "Gọi điện tư vấn",
    "inquiryId": 0, này là yêu cầu (cơ hội) khi nào tạo từ bên màn hình yêu cầu mới truyền về, có thể có hoặc không
    "customerId": 40687, // id của khách hàng tiềm năng
    "type": 1,// theo Enum ActivityTypeEnum
    "status": 1, //Chưa bắt đầu NEW = 1,// Đang thực hiện INPROGRESS = 2,// Tạm hoãn HOLD = 3,// Hoàn thành COMPLETED = 4,// Đã huỷ bỏ CANCELED = 5,
    "picId": 11964, //Người liên quan
    "relatedPersonIds": [ // người liên hệ
        11964 
    ],
    "phoneNumber": "0123456789", // này type = 1
    //"callResult": "Gọi xác nhận" //  này type = 1 
    //"place": "string",  //  này type = 3
    //"missionType": 1  //  này type =2
}

  */

  //create or update activity
  public async createOrUpdateActivity(body: any) {
    const res = await http.post(
      "/api/services/app/ExistingActivity/CreateOrUpdate",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  //delete activity
  public async deleteActivity(id: number) {
    const res = await http.delete("/api/services/app/ExistingActivity/Delete", {
      params: { id },
    })
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("DELETE_SUCCESSFULLY")
    )
    return res.data.result
  }

  //GetAllEntityContactInfo
  public async getAllEntityContactInfo(params: any) {
    const res = await http.get("/api/services/app/ExistingCustomer/GetAllEntityContactInfo", {
      params,
    })
    const { result } = res.data
    return result
  }

  //create or update entity contact info
  public async createOrUpdateEntityContactInfo(body: any) {
    const res = await http.post(
      "api/services/app/ExistingCustomer/CreateOrUpdateEntityContractInfo",
      body
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

  // get entity contact info
  public async getEntityContactInfo(existingCustomerId: number) {
    const res = await http.get("api/services/app/ExistingCustomer/GetEntityContactInfo", {
      params: { existingCustomerId },
    })
    const { result } = res.data
    return result
  }

  public async validateBeforeImportEntityContactInfo(file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post("api/Imports/ExistingCustomer/ValidateBeforeImportEntityContactInfo", formData, {
      headers: {
        "content-type": "multipart/form-data",
      },
    })
    return res.data.result
  }
  
  public async importEntityContactInfo(body: any): Promise<any> {
    const res = await http.post("api/services/app/ExistingCustomer/ImportEntityContactInfo", body)
    notifySuccess(LNotification("SUCCESS"), LNotification("IMPORT_SUCCESSFULLY"))
    return res.data.result
  }
}

export default new CustomerService()
